import { snapshot, MaskInputOptions } from '@xysfe/memento-snapshot';
import initObservers, { initOtherObserver } from './observer';
import XFix, { CHANGE_STATE_EVENT } from './xfix';
import {
  mirror,
  on,
  getWindowWidth,
  getWindowHeight,
  polyfill,
} from '../utils';
import {
  EventType,
  event,
  eventWithTime,
  recordOptions,
  IncrementalSource,
  listenerHandler,
  fullSnapshotEvent,
  OtherSource,
  canvasMutationParam,
  mousePosition,
  mouseInteractionParam,
  UserDefinedEvent
} from '../types';
import { CanvasManager } from './observers/canvas/canvas-manager';

function wrapEvent(e: event): eventWithTime {
  return {
    ...e,
    timestamp: (e as any).timestamp || Date.now(),
  };
}

function disposeMousemovePosition (positions: mousePosition[], ignoreMousemove: boolean): any[] {
  if (!ignoreMousemove) {
    return positions
  }
  return positions.map((item: Record<string, any>) => {
    return {
      ...item,
      x: -40,
      y: -40
    }
  })
}

function disposeMouseInteraction (d: mouseInteractionParam, ignoreMousemove: boolean): event {
  if (ignoreMousemove) {
    d.x = -40
    d.y = -40
  } 
  return {
    type: EventType.IncrementalSnapshot,
    data: {
      source: IncrementalSource.MouseInteraction,
      ...d
    } 
  }
}

let wrappedEmit!: (e: eventWithTime, isCheckout?: boolean) => void;

function takeFullSnapshot<T = eventWithTime>(options: recordOptions<T> = {}, isCheckout = false, tag?: string) {
  const {
    emit,
    blockClass = 'mem-block',
    ignoreClass = 'mem-ignore',
    inlineStylesheet = true,
    maskAllInputs = false,
    blockElements = [],
    recordCanvas = false,
    ignoreMousemove = false
  } = options;
  if (!wrappedEmit) {
    throw new Error('please take full snapshot after start recording');
  }
  wrappedEmit(
    wrapEvent({
      type: EventType.Meta,
      data: {
        href: window.location.href,
        width: getWindowWidth(),
        height: getWindowHeight(),
        ratio: window.devicePixelRatio || 1,
      },
    }),
    isCheckout,
  );
  const [node, idNodeMap] = snapshot(
    document,
    blockClass,
    inlineStylesheet,
    maskAllInputs,
    blockElements,
    recordCanvas,
    ignoreMousemove
  );

  if (!node) {
    return console.warn('Failed to snapshot the document');
  }

  mirror.map = idNodeMap;
  const data: fullSnapshotEvent = {
    type: EventType.FullSnapshot,
    data: {
      href: window.location.href,
      width: getWindowWidth(),
      height: getWindowHeight(),
      ratio: window.devicePixelRatio || 1,
      node,
      initialOffset: {
        left:
          window.pageXOffset !== undefined
            ? window.pageXOffset
            : document?.documentElement.scrollLeft ||
              document?.body?.parentElement?.scrollLeft ||
              document?.body.scrollLeft ||
              0,
        top:
          window.pageYOffset !== undefined
            ? window.pageYOffset
            : document?.documentElement.scrollTop ||
              document?.body?.parentElement?.scrollTop ||
              document?.body.scrollTop ||
              0,
      },
    },
  };
  if (tag) {
    data.tag = tag;
  }
  const event = wrapEvent(data);

  wrappedEmit(event);
  if (emit) {
    emit(event as unknown as T, isCheckout);
  }
}

function record<T = eventWithTime>(
  options: recordOptions<T> = {},
): listenerHandler | undefined {
  const {
    emit,
    checkoutEveryNms,
    checkoutEveryNth,
    blockClass = 'mem-block',
    ignoreClass = 'mem-ignore',
    asyncClass,
    blockElements = [],
    inlineStylesheet = true,
    maskAllInputs,
    maskInputOptions: _maskInputOptions,
    hooks,
    packFn,
    sampling = {},
    mousemoveWait,
    recordCanvas = false,
    ignoreMousemove = false,
    onlyRecordFullSnapshot = false
  } = options;
  // runtime checks for user options
  if (!emit) {
    throw new Error('emit function is required');
  }
  // move departed options to new options
  if (mousemoveWait !== undefined && sampling.mousemove === undefined) {
    sampling.mousemove = mousemoveWait;
  }

  const maskInputOptions: MaskInputOptions =
  maskAllInputs === true
    ? {
      color: true,
      date: true,
      'datetime-local': true,
      email: true,
      month: true,
      number: true,
      range: true,
      search: true,
      tel: true,
      text: true,
      time: true,
      url: true,
      week: true,
      textarea: true,
      select: true,
    }
    : _maskInputOptions !== undefined
    ? _maskInputOptions
    : {};

  polyfill();

  let lastFullSnapshotEvent: eventWithTime;
  let incrementalSnapshotCount = 0;

  const opt = {
    ...options,
    emit() {
      //
    },
  };

  wrappedEmit = (e: eventWithTime, isCheckout?: boolean) => {
    emit(((packFn ? packFn(e) : e) as unknown) as T, isCheckout);
    if (e.type === EventType.FullSnapshot) {
      lastFullSnapshotEvent = e;
      incrementalSnapshotCount = 0;
    } else if (e.type === EventType.IncrementalSnapshot) {
      incrementalSnapshotCount++;
      const exceedCount =
        checkoutEveryNth && incrementalSnapshotCount >= checkoutEveryNth;
      const exceedTime =
        checkoutEveryNms &&
        e.timestamp - lastFullSnapshotEvent.timestamp > checkoutEveryNms;
      if (exceedCount || exceedTime) {
        takeFullSnapshot(opt, true);
      }
    }
  };

  const wrappedCanvasMutationEmit = (p: canvasMutationParam) =>
    wrappedEmit(
      wrapEvent({
        type: EventType.IncrementalSnapshot,
        data: {
          source: IncrementalSource.CanvasMutation,
          ...p,
        },
      }),
    );
  
  const canvasManager = new CanvasManager({
    recordCanvas,
    mutationCb: wrappedCanvasMutationEmit,
    win: window,
    blockClass,
    mirror,
    sampling: sampling.canvas,
  });

  try {
    XFix.getInstance();

    const handlers: listenerHandler[] = [];
    let url = window.location.href;

    const pageChange = (force?: Boolean) => {
      if (force || url !== window.location.href) {
        url = window.location.href;
        wrappedEmit(
          wrapEvent({
            type: EventType.PageChange,
            data: {
              url,
            },
          }),
        );
      }
    };

    const checkPageChange = () => {
      pageChange(false);
    };

    if (!onlyRecordFullSnapshot) {
      handlers.push(
        on(CHANGE_STATE_EVENT, checkPageChange, window),
      );
      handlers.push(
        on('hashchange', checkPageChange, window),
      );
      handlers.push(
        on('popstate', checkPageChange, window),
      );
      handlers.push(
        on('pageshow', (event: PageTransitionEvent) => {
          if (event.persisted) {
            pageChange(true);
          }
        }, window),
      );
    }

    let delayEvents: any = [];
    let hasInited = false

    if (!onlyRecordFullSnapshot) {
      handlers.push(
        initOtherObserver(
          {
            consoleCb: (c: any) => {
              const eventData: any = {
                type: EventType.Other,
                data: {
                  source: OtherSource.Console,
                  sourceData: c,
                },
              }
              if (hasInited) {
                wrappedEmit(
                  wrapEvent(eventData)
                )
              } else {
                delayEvents.push(eventData)
              }
            },
            networkCb: (n: any) => {
              const eventData: any = {
                type: EventType.Other,
                data: {
                  source: OtherSource.Network,
                  sourceData: n,
                },
              }
              if (hasInited) {
                wrappedEmit(
                  wrapEvent(eventData)
                )
              } else {
                delayEvents.push(eventData)
              }
            },
          },
          hooks
        )
      )
    }
    handlers.push(
      on('DOMContentLoaded', () => {
        wrappedEmit(
          wrapEvent({
            type: EventType.DomContentLoaded,
            data: {},
          }),
        );
      }),
    );
    const init = () => {
      takeFullSnapshot(opt);
      // emit delayEvents
      for (const eventData of delayEvents) {
        wrappedEmit(
          wrapEvent(eventData)
        )
      }
      hasInited = true
      delayEvents = []

      if (!onlyRecordFullSnapshot) {
        handlers.push(
          initObservers(
            {
              mutationCb: (m) =>
                wrappedEmit(
                  wrapEvent({
                    type: EventType.IncrementalSnapshot,
                    data: {
                      source: IncrementalSource.Mutation,
                      ...m,
                    },
                  }),
                ),
              mousemoveCb: (positions, source) =>
                wrappedEmit(
                  wrapEvent({
                    type: EventType.IncrementalSnapshot,
                    data: {
                      source,
                      positions: disposeMousemovePosition(positions, ignoreMousemove),
                    },
                  }),
                ),
              mouseInteractionCb: (d) =>
                wrappedEmit(
                  wrapEvent(disposeMouseInteraction(d, ignoreMousemove)),
                ),
              scrollCb: (p) =>
                wrappedEmit(
                  wrapEvent({
                    type: EventType.IncrementalSnapshot,
                    data: {
                      source: IncrementalSource.Scroll,
                      ...p,
                    },
                  }),
                ),
              viewportResizeCb: (d) =>
                wrappedEmit(
                  wrapEvent({
                    type: EventType.IncrementalSnapshot,
                    data: {
                      source: IncrementalSource.ViewportResize,
                      ...d,
                    },
                  }),
                ),
              inputCb: (v) =>
                wrappedEmit(
                  wrapEvent({
                    type: EventType.IncrementalSnapshot,
                    data: {
                      source: IncrementalSource.Input,
                      ...v,
                    },
                  }),
                ),
              mediaInteractionCb: (p) =>
                wrappedEmit(
                  wrapEvent({
                    type: EventType.IncrementalSnapshot,
                    data: {
                      source: IncrementalSource.MediaInteraction,
                      ...p,
                    },
                  }),
                ),
              canvasMutationCb: wrappedCanvasMutationEmit,
              styleSheetRuleCb: (r) =>
                wrappedEmit(
                  wrapEvent({
                    type: EventType.IncrementalSnapshot,
                    data: {
                      source: IncrementalSource.StyleSheetRule,
                      ...r,
                    },
                  }),
                ),
              userDefinedEventCb: (p) =>
                wrappedEmit(
                  wrapEvent({
                    type: EventType.IncrementalSnapshot,
                    data: {
                      source: IncrementalSource.UserDefinedEvent,
                      ...p,
                    },
                  }),
                ),
              blockClass,
              blockElements,
              ignoreClass,
              asyncClass,
              maskInputOptions,
              inlineStylesheet,
              sampling,
              recordCanvas,
              canvasManager
            },
            hooks,
          ),
        );
      }
    };
    if (
      document.readyState === 'interactive' ||
      document.readyState === 'complete'
    ) {
      init();
    } else {
      handlers.push(
        on(
          'load',
          () => {
            wrappedEmit(
              wrapEvent({
                type: EventType.Load,
                data: {},
              }),
            );
            init();
          },
          window,
        ),
      );
    }
    return () => {
      handlers.forEach((h) => h());
    };
  } catch (error) {
    // TODO: handle internal error
    console.warn(error);
  }
}

record.addCustomEvent = <T>(tag: string, payload: T) => {
  if (!wrappedEmit) {
    throw new Error('please add custom event after start recording');
  }
  wrappedEmit(
    wrapEvent({
      type: EventType.Custom,
      data: {
        tag,
        payload,
      },
    }),
  );
};

record.takeFullSnapshot = <T>(options: recordOptions<T> = {}, tag?: string) => {
  if (!wrappedEmit) {
    return;
  }
  takeFullSnapshot(options, false, tag);
};

export default record;
